import { useAppSelector } from "@app/hooks"
import { TabIdentifier } from "@features/app-tabs/tabs.selector"
import { selectShouldShowProductionWarning } from "@features/ui/uiSelector"
import { Box, Stack } from "@mui/material"
import { useState } from "react"
import AppHeader from "./header/appHeader"
import { NavigationRail } from "./sidebar/navigationRail"
import {
  MainSidebarHeader,
  SIDEBAR_PX_WIDTH,
  useMainSidebarItems,
} from "./sidebar/mainSidebar"

const COMPACT_PX_WIDTH = 80
// Compact rail is the default for everyone; a stored 'true' means the user
// explicitly opted into the expanded layout. No stored value = compact.
const NAV_EXPANDED_STORAGE_KEY = "nav.expanded.v1"
const ANIMATION_SECONDS = 0.2

export function AppTabsContainer(props: {
  selectedTab: TabIdentifier | undefined
  children?: React.ReactNode
}) {
  const [isExpanded, setIsExpanded] = useState(
    () => localStorage.getItem(NAV_EXPANDED_STORAGE_KEY) === "true",
  )
  const shouldShowFrame = useAppSelector(selectShouldShowProductionWarning)
  const mainSideBarItems = useMainSidebarItems(props.selectedTab)
  const railWidth = isExpanded ? SIDEBAR_PX_WIDTH : COMPACT_PX_WIDTH

  const toggle = () => {
    setIsExpanded((prev) => {
      const next = !prev
      localStorage.setItem(NAV_EXPANDED_STORAGE_KEY, next ? "true" : "false")
      return next
    })
  }

  return (
    <Stack
      direction="row"
      position="relative"
      sx={{
        height: 1,
        width: 1,
        border: shouldShowFrame ? "red solid 10px" : "none",
        bgcolor: "white",
      }}
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        height={1}
        sx={{
          width: `${railWidth}px`,
          overflow: "hidden",
          bgcolor: "eTypes.sand25",
          transition: `width ease-in-out ${ANIMATION_SECONDS}s`,
        }}
      >
        <NavigationRail
          header={<MainSidebarHeader isMinimized={!isExpanded} />}
          items={mainSideBarItems}
          isExpanded={isExpanded}
          onToggle={toggle}
        />
      </Box>
      <Stack
        height={1}
        sx={{
          // Fill the space beside the rail exactly — NOT width:100% + margin
          // (that overflows) and NOT alignItems:flex-start (that shrinks the
          // page to its content width and left-aligns it, leaving dead space
          // on wide screens). Children stretch to full width by default.
          width: `calc(100% - ${railWidth}px)`,
          ml: `${railWidth}px`,
          overflow: "hidden",
          transition: `margin ease-in-out ${ANIMATION_SECONDS}s, width ease-in-out ${ANIMATION_SECONDS}s`,
        }}
      >
        <AppHeader />
        {props.children}
      </Stack>
    </Stack>
  )
}
